import { z } from "zod";
import { DatabaseSequenceSchema, DecimalStringSchema, UuidSchema } from "./primitives.js";
export const WorkspaceDomainEventSchema = z
    .object({
    workspaceId: UuidSchema,
    sequence: DatabaseSequenceSchema,
    eventId: UuidSchema,
    type: z.string().min(1).max(200),
    aggregateId: UuidSchema,
    aggregateRevision: DatabaseSequenceSchema,
    payload: z.unknown(),
})
    .strict();
export const AppSocketHelloSchema = z
    .object({
    type: z.literal("HELLO"),
    workspaces: z
        .array(z
        .object({
        workspaceId: UuidSchema,
        lastSequence: DatabaseSequenceSchema,
    })
        .strict())
        .max(100),
})
    .strict()
    .superRefine((value, context) => {
    const workspaceIds = new Set();
    for (const [index, workspace] of value.workspaces.entries()) {
        if (workspaceIds.has(workspace.workspaceId)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Workspace cursors must be unique",
                path: ["workspaces", index, "workspaceId"],
            });
        }
        workspaceIds.add(workspace.workspaceId);
    }
});
export const CollabUpdateFrameSchema = z
    .object({
    entityId: UuidSchema,
    collabSessionId: UuidSchema,
    clientUpdateSequence: DatabaseSequenceSchema,
    updateBase64: z.string().regex(/^[A-Za-z0-9+/]*={0,2}$/),
})
    .strict();
export const CollabPersistedFrameSchema = z
    .object({
    type: z.literal("PERSISTED"),
    entityId: UuidSchema,
    collabSessionId: UuidSchema,
    clientUpdateSequence: DatabaseSequenceSchema,
    stateVectorBase64: z.string().regex(/^[A-Za-z0-9+/]*={0,2}$/),
})
    .strict();
export const CollabTicketCommandSchema = z
    .object({
    workspaceId: UuidSchema,
    entityId: UuidSchema,
    permission: z.enum(["view", "edit"]),
})
    .strict();
export const CollabTicketResponseSchema = z
    .object({
    ticket: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    workspaceId: UuidSchema,
    entityId: UuidSchema,
    permission: z.enum(["view", "edit"]),
    expiresAt: z.string().datetime({ offset: true }),
})
    .strict();
export const CollabSocketHelloSchema = z
    .object({
    type: z.literal("HELLO"),
    ticket: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
})
    .strict();
export const CollabSaveFrameSchema = z
    .object({
    type: z.literal("SAVE"),
    commandId: UuidSchema,
    revision: DatabaseSequenceSchema,
    bodyMarkdown: z.string().max(1_048_576),
    stateVectorBase64: z.string().regex(/^[A-Za-z0-9+/]*={0,2}$/),
})
    .strict();
export const AppAccountStateFrameSchema = z
    .object({
    type: z.literal("ACCOUNT_STATE"),
    actorAccountId: UuidSchema,
    effectiveAccountId: UuidSchema,
    simulationSessionId: UuidSchema.nullable(),
    simulated: z.boolean(),
})
    .strict();
export const AppEventBatchFrameSchema = z
    .object({
    type: z.literal("EVENT_BATCH"),
    workspaceId: UuidSchema,
    events: z.array(WorkspaceDomainEventSchema).min(1).max(100),
    nextSequence: DatabaseSequenceSchema,
    hasMore: z.boolean(),
})
    .strict()
    .superRefine((value, context) => {
    let previousSequence;
    for (const [index, event] of value.events.entries()) {
        if (event.workspaceId !== value.workspaceId) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Batch events must belong to the frame workspace",
                path: ["events", index, "workspaceId"],
            });
        }
        const sequence = BigInt(event.sequence);
        if (previousSequence !== undefined && sequence <= previousSequence) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Batch event sequences must be strictly increasing",
                path: ["events", index, "sequence"],
            });
        }
        previousSequence = sequence;
    }
    const lastEvent = value.events.at(-1);
    if (lastEvent !== undefined && lastEvent.sequence !== value.nextSequence) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Batch cursor must equal the final event sequence",
            path: ["nextSequence"],
        });
    }
});
export const AppInvalidateFrameSchema = z
    .object({
    type: z.literal("INVALIDATE"),
    workspaceId: UuidSchema,
    reason: z.enum(["CURSOR_GAP", "BACKLOG_OVERFLOW"]),
    resumeSequence: DatabaseSequenceSchema,
})
    .strict();
export const AppSocketServerFrameSchema = z.discriminatedUnion("type", [
    AppAccountStateFrameSchema,
    AppEventBatchFrameSchema,
    AppInvalidateFrameSchema,
]);
export const CollabSyncedUpdateSchema = z
    .object({
    collabSessionId: UuidSchema,
    clientUpdateSequence: DatabaseSequenceSchema,
    updateBase64: z.string().regex(/^[A-Za-z0-9+/]*={0,2}$/),
})
    .strict();
export const CollabSyncedFrameSchema = z
    .object({
    type: z.literal("SYNCED"),
    entityId: UuidSchema,
    snapshotBase64: z.string().regex(/^[A-Za-z0-9+/]*={0,2}$/),
    stateVectorBase64: z.string().regex(/^[A-Za-z0-9+/]*={0,2}$/),
    updates: z.array(CollabSyncedUpdateSchema).max(10_000),
})
    .strict();
export const CollabSavedFrameSchema = z
    .object({
    type: z.literal("SAVED"),
    noteId: UuidSchema,
    revision: DatabaseSequenceSchema,
})
    .strict();
export const CollabSocketServerFrameSchema = z.discriminatedUnion("type", [
    CollabSyncedFrameSchema,
    CollabPersistedFrameSchema,
    CollabSavedFrameSchema,
]);
//# sourceMappingURL=realtime.js.map